// frontend/src/pages/Register.js
import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import '../styles/Auth.css';
import { AuthContext } from '../context/AuthContext';
import zxcvbn from 'zxcvbn';
import LanguageSelector from '../components/LanguageSelector';
import { useI18n } from '../i18n';
import AntahkaranaLogo from '../components/AntahkaranaLogo';

function Register() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { t } = useI18n();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSensitiveData, setAcceptedSensitiveData] = useState(false);
  const [acceptedExternalAI, setAcceptedExternalAI] = useState(false);
  const [error, setError] = useState('');

  // Password Strength State
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState('');

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Evaluate strength
    const result = zxcvbn(newPassword);
    setPasswordStrength(result.score); // 0 to 4
    setPasswordFeedback(result.feedback.warning || result.feedback.suggestions[0]);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms || !acceptedSensitiveData || !acceptedExternalAI) {
      setError(t('auth.acceptAll'));
      return;
    }

    // Client-side Validation
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      setError(t('auth.invalidUsername'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(String(email).toLowerCase())) {
        setError(t('auth.invalidEmail'));
        return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!passwordRegex.test(password)) {
      setError(t('auth.weakPassword'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    try {
      // Ruta registro en backend: POST /api/auth/register
      const response = await api.post('/auth/register', {
        username,
        email,
        password,
        termsAccepted: true
      });
      const { token } = response.data;
      localStorage.setItem('token', token);
      login(token);
      // Redirigir a página principal o donde corresponda
      navigate('/');
    } catch (err) {
      console.error('Register error:', err);
      const msg = err.response?.data?.error || t('auth.registerError');
      setError(msg);
    }
  };

  const handleGoogleLogin = () => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';
    // Redirige al backend, el cual nos devolverá a esta misma página con #token=...
    window.open(`${API_URL}/api/auth/google`, "_self");
  };

  const getStrengthColor = () => {
      switch(passwordStrength) {
          case 0: return 'darkred';
          case 1: return 'red';
          case 2: return 'orange';
          case 3: return 'yellowgreen';
          case 4: return 'green';
          default: return 'gray';
      }
  }

  return (
    <div className="auth-container">
      <LanguageSelector compact className="auth-language-selector" />

      <div className="auth-top-right-stack">
        <div className="auth-top-nav">
          <button
            type="button"
            className="auth-cyber-btn"
            onClick={() => navigate('/login')}
          >
            {t('auth.login')}
          </button>
          <span className="auth-nav-caption">{t('auth.alreadyUser')}</span>
        </div>
      </div>

      <AntahkaranaLogo className="auth-logo-header" size={220} style={{ display: 'block', margin: '0 auto 20px auto' }} />

      <form className="auth-form" onSubmit={handleRegister}>
        <input
          type="text"
          placeholder={t('auth.username')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder={t('auth.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder={t('auth.password')}
          value={password}
          onChange={handlePasswordChange}
          required
        />
        {/* Strength Meter */}
        {password && (
            <div style={{ marginTop: '5px', marginBottom: '10px', width: '100%' }}>
                <div style={{ height: '5px', width: '100%', backgroundColor: '#eee', borderRadius: '3px' }}>
                    <div style={{
                        height: '100%',
                        width: `${(passwordStrength + 1) * 20}%`,
                        backgroundColor: getStrengthColor(),
                        borderRadius: '3px',
                        transition: 'width 0.3s ease-in-out'
                    }}></div>
                </div>
                <small style={{ color: getStrengthColor(), display: 'block', textAlign: 'left', marginTop: '5px' }}>
                    {t('auth.strength')[passwordStrength]}
                </small>
                {passwordFeedback && <div style={{ fontSize: '0.8rem', color: '#ccc', textAlign: 'left' }}>{passwordFeedback}</div>}
            </div>
        )}

        <input
          type="password"
          placeholder={t('auth.repeatPassword')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />

        <div className="terms-container" style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left', marginTop: '10px' }}>
          <label className="terms-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              className="terms-checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{ marginTop: '4px' }}
            />
            <span>
              {t('auth.acceptTermsPrefix')} <Link to="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">{t('auth.terms')}</Link> {t('auth.acceptTermsJoin')} <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">{t('auth.privacy')}</Link>.
            </span>
          </label>

          <label className="terms-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              className="terms-checkbox"
              checked={acceptedSensitiveData}
              onChange={(e) => setAcceptedSensitiveData(e.target.checked)}
              style={{ marginTop: '4px' }}
            />
            <span>
              {t('auth.sensitiveConsent')}
            </span>
          </label>

          <label className="terms-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              className="terms-checkbox"
              checked={acceptedExternalAI}
              onChange={(e) => setAcceptedExternalAI(e.target.checked)}
              style={{ marginTop: '4px' }}
            />
            <span>
              {t('auth.externalAiConsent')}
            </span>
          </label>

          <p style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic', marginTop: '10px', lineHeight: '1.4' }}>
            {t('auth.productConsent')}
          </p>
        </div>

        <div className="auth-action-group">
          <button
            type="submit"
            className="auth-cyber-btn purple-variant"
            disabled={!acceptedTerms || !acceptedSensitiveData || !acceptedExternalAI}
          >
            {t('auth.register')}
          </button>
        </div>
      </form>

      <div className="google-login-container" style={{ marginTop: '20px', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <button onClick={handleGoogleLogin} className="auth-google-btn">
          <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" style={{ width: '20px', height: '20px' }} />
          {t('auth.googleRegister')}
        </button>
      </div>

      {error && <p className="auth-error">{error}</p>}
    </div>
  );
}

export default Register;
